import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useRef, useState, useMemo } from "react";
import { CHANGE_TYPE_ADD, CHANGE_TYPE_REMOVE, StagedChange } from "../../domain/feature-flags";
import { CustomerMap } from "../../domain/customer";

export type ConfirmApplyDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  changes: StagedChange[];
  customerMap: CustomerMap;
  isApplying: boolean;
};

type GroupedChanges = Record<string, Array<{ cxId: string; customerName: string }>>;

export function ConfirmApplyDialog({
  isOpen,
  onClose,
  onConfirm,
  changes,
  customerMap,
  isApplying,
}: ConfirmApplyDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  function getCustomerName(cxId: string): string {
    const customer = customerMap[cxId];
    return customer?.rootCustomer.email ?? cxId;
  }

  function handleClose() {
    setIsConfirmed(false);
    onClose();
  }

  function handleConfirm() {
    onConfirm();
    setIsConfirmed(false);
  }

  // Group changes by flag name
  const groupedAdditions = useMemo(() => {
    const grouped: GroupedChanges = {};
    changes
      .filter(c => c.type === CHANGE_TYPE_ADD)
      .forEach(change => {
        if (!grouped[change.flagName]) {
          grouped[change.flagName] = [];
        }
        grouped[change.flagName].push({
          cxId: change.cxId,
          customerName: getCustomerName(change.cxId),
        });
      });
    return grouped;
  }, [changes, customerMap]);

  const groupedRemovals = useMemo(() => {
    const grouped: GroupedChanges = {};
    changes
      .filter(c => c.type === CHANGE_TYPE_REMOVE)
      .forEach(change => {
        if (!grouped[change.flagName]) {
          grouped[change.flagName] = [];
        }
        grouped[change.flagName].push({
          cxId: change.cxId,
          customerName: getCustomerName(change.cxId),
        });
      });
    return grouped;
  }, [changes, customerMap]);

  const additionCount = changes.filter(c => c.type === CHANGE_TYPE_ADD).length;
  const removalCount = changes.filter(c => c.type === CHANGE_TYPE_REMOVE).length;
  const additionEntries = Object.entries(groupedAdditions);
  const removalEntries = Object.entries(groupedRemovals);

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose} size="xl">
      <AlertDialogOverlay>
        <AlertDialogContent maxW="600px" maxH="85vh" display="flex" flexDirection="column">
          <AlertDialogHeader
            fontSize="lg"
            fontWeight="bold"
            flexShrink={0}
            bg="gray.50"
            borderBottomWidth="1px"
            borderColor="gray.200"
          >
            Apply Changeset
          </AlertDialogHeader>

          <AlertDialogBody overflowY="auto" flex="1">
            <VStack align="stretch" gap={4}>
              <Text>
                You are about to apply <strong>{changes.length}</strong> change(s) to the feature
                flags:
              </Text>

              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                {additionCount > 0 && (
                  <Box>
                    <Box
                      px={3}
                      py={1.5}
                      bg="green.50"
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="green.600"
                        textTransform="uppercase"
                      >
                        Adding ({additionCount})
                      </Text>
                    </Box>
                    <VStack align="stretch" gap={0}>
                      {additionEntries.map(([flagName, customers]) => (
                        <Box key={flagName} px={3} py={1}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.700">
                            {flagName}
                          </Text>
                          {customers.map(({ cxId, customerName }) => (
                            <Flex key={cxId} fontSize="xs" color="green.600" ml={2} gap={1}>
                              <Text>+ {customerName}</Text>
                              <Text color="gray.400" fontFamily="mono" fontSize="2xs">
                                {cxId}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {additionCount > 0 && removalCount > 0 && <Divider borderColor="gray.300" />}

                {removalCount > 0 && (
                  <Box>
                    <Box px={3} py={1.5} bg="red.50" borderBottomWidth="1px" borderColor="gray.200">
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="red.600"
                        textTransform="uppercase"
                      >
                        Removing ({removalCount})
                      </Text>
                    </Box>
                    <VStack align="stretch" gap={0}>
                      {removalEntries.map(([flagName, customers]) => (
                        <Box key={flagName} px={3} py={1}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.700">
                            {flagName}
                          </Text>
                          {customers.map(({ cxId, customerName }) => (
                            <Flex key={cxId} fontSize="xs" color="red.600" ml={2} gap={1}>
                              <Text>− {customerName}</Text>
                              <Text color="gray.400" fontFamily="mono" fontSize="2xs">
                                {cxId}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </Box>

              <Box
                p={3}
                bg="yellow.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="yellow.200"
              >
                <Text fontSize="sm" color="yellow.800">
                  ⚠️ This action will update production feature flags. Make sure you have received
                  approval for these changes.
                </Text>
              </Box>

              <Checkbox
                isChecked={isConfirmed}
                onChange={e => setIsConfirmed(e.target.checked)}
                colorScheme="blue"
              >
                <Text fontSize="sm">I confirm these changes have been reviewed and approved</Text>
              </Checkbox>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter
            flexShrink={0}
            bg="gray.50"
            borderTopWidth="1px"
            borderColor="gray.200"
          >
            <Button ref={cancelRef} onClick={handleClose} isDisabled={isApplying}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleConfirm}
              ml={3}
              isDisabled={!isConfirmed}
              isLoading={isApplying}
            >
              Apply Changeset
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
